import React, { useEffect, useState } from "react";
import Header from "../../components/header/Header";
import MuiAppBar from "@mui/material/AppBar";
import SideMenu from "../../components/sidemenu/SideMenu";
import { Box, CircularProgress, Drawer, styled } from "@mui/material";
import { getToken, onMessage } from "firebase/messaging";
import { messaging } from "../../firebase";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { getOrder, updateOrderStatus } from "../../store/reducers/orders";
import { useTranslation } from "react-i18next";
import {
  firebaseToken,
  getAllNotification,
  readAllNotification,
} from "../../store/reducers/notification";
import NewCheckInReqModal from "../../components/NotificationModal/NewCheckInReqModal";
import { FIREBASE_VAPID_KEY } from "../../utils/constants/key";
import { useAbly } from "../../components/app/AblyWrapper";

const drawerWidth = 24;

const AppBar = styled(MuiAppBar)({
  width: `calc(100% - ${drawerWidth}rem)`,
  backgroundColor: "#fff",
  boxShadow: "none",
  marginLeft: "24rem",
});

const PageLayout = ({
  children,
  headerTitle,
  showBackBtn,
  showSearchField = true,
  handleChange,
  fetchDashboardData = () => {},
  statusConfig,
  order_id,
  fetchOrderData,
  showNotification = true,
}) => {
  const { loading } = useSelector(state => state.orders.getOrderApi);
  const [data, setData] = useState();
  const [currentUserType, setCurrentUserType] = useState(0);
  const [openNewCheckInModal, setOpenNewCheckInModal] = useState(false);
  const dispatch = useDispatch();
  const [AllNotification, setAllNotification] = useState();
  const { t } = useTranslation();
  const { isNotificationFetched } = useSelector(state => state.notification);
  const { event, publish, clearEvent } = useAbly();

  // Queue system for multiple popups
  const [notificationQueue, setNotificationQueue] = useState([]);
  const [currentNotification, setCurrentNotification] = useState(null);

  const onCloseNewCheckIn = () => {
    setOpenNewCheckInModal(false);
    setCurrentNotification(null);
  };

  const processNotification = async (notificationData) => {
    setCurrentNotification(notificationData);
    setData(notificationData);
    setOpenNewCheckInModal(true);
    
    // Set user type based on the data
    if (notificationData?.receiver === null) {
      setCurrentUserType(0);
    } else {
      setCurrentUserType(1);
    }
  };

  const addToQueue = (notificationData) => {
    setNotificationQueue(prev => {
      if (!currentNotification && prev.length === 0) {
        processNotification(notificationData);
        return prev;
      }
      return [...prev, notificationData];
    });
  };

  const fetchAllNotification = () => {
    dispatch(getAllNotification()).then(res => {
      if (!res.error) {
        setAllNotification(res.payload);
      }
    });
  };

  const createToken = async token => {
    localStorage.setItem("firebaseToken", token);
    dispatch(
      firebaseToken({
        registration_id: token,
        type: "web",
      }),
    );
  };

  const requestPermission = async ({ loadToken }) => {
    const permission = await Notification.requestPermission();
    if (
      permission === "denied" &&
      !localStorage.getItem("showPermissionDeniedAlert")
    ) {
      console.log("Permission denied");
      localStorage.setItem("showPermissionDeniedAlert", true);
      alert(t("block_notification_alert"));
    }
    if (permission === "granted") {
      console.log("Permission granted");
      const resolvedMessaging = await messaging;
      if (!resolvedMessaging) return;
      const token = await getToken(resolvedMessaging, {
        vapidKey: FIREBASE_VAPID_KEY,
      }).catch(err => {
        console.log("Error", err);
      });
      if (!token) {
        if (loadToken < 5) {
          requestPermission({ loadToken: loadToken + 1 });
          return;
        }
      }
      createToken(token);
    }
  };

  useEffect(() => {
    if (isNotificationFetched === false) {
      fetchAllNotification();
    }
    if (localStorage.getItem("firebaseToken") === null) {
      console.log("Requesting permission");
      requestPermission({ loadToken: 0 });
    }

    const handleSwMessage = event => {
      if (event.data?.type === 'FIREBASE_BACKGROUND_MESSAGE') {
        toast.info(`${event.data.title}: ${event.data.body}`, {
          position: 'top-right',
          autoClose: 5000,
        });
      }
      if (event.data?.type === 'NOTIFICATION_CLICK' && event.data?.id) {
        fetchData(event.data.id).then(notificationData => {
          if (notificationData) addToQueue(notificationData);
        });
      }
    };

    // Handle app opened from a notification click (app was closed)
    const urlParams = new URLSearchParams(window.location.search);
    const orderId = urlParams.get('orderId');
    if (orderId) {
      fetchData(orderId).then(notificationData => {
        if (notificationData) addToQueue(notificationData);
      });
      // Remove the param from URL without reload
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);
    }
    navigator.serviceWorker?.addEventListener('message', handleSwMessage);

    let unsubscribeForeground = () => {};
    // Foreground messages (app is open/focused)
    messaging.then(resolvedMessaging => {
      if (!resolvedMessaging) return;
      unsubscribeForeground = onMessage(resolvedMessaging, payload => {
        console.log('[onMessage] Foreground message received:', payload);
        const title = payload.data?.title || payload.notification?.title || 'New notification';
        const body = payload.data?.body || payload.notification?.body || '';
        toast.info(`${title}: ${body}`, {
          position: 'top-right',
          autoClose: 5000,
        });
      });
    });

    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleSwMessage);
      unsubscribeForeground();
    };
  }, []);

  const fetchData = async id => {
    return await dispatch(getOrder(id)).then(res => {
      if (!res.error) {
        return res?.payload;
      }
      return null;
    });
  };

  useEffect(() => {
    if (!event) return;
    
    console.log("Ably event received:", event);
    let payload = event?.data;
    
    const handleNotification = async () => {
      try {
        const notificationData = await fetchData(payload?.id);
        if (notificationData) {
          addToQueue(notificationData);
        }
        
        fetchDashboardData(statusConfig);
        fetchAllNotification();
        
        if (payload?.id === order_id) {
          fetchOrderData(order_id);
        }
      } catch (error) {
        console.error("Error handling notification:", error);
      } finally {
        clearEvent();
      }
    };
    
    handleNotification();
  }, [event]);

  // React-driven queue: process next item when currentNotification clears
  useEffect(() => {
    if (!currentNotification && notificationQueue.length > 0) {
      const next = notificationQueue[0];
      setNotificationQueue(prev => prev.slice(1));
      processNotification(next);
    }
  }, [currentNotification, notificationQueue]);

  useEffect(() => {
    if (data?.receiver === null) {
      setCurrentUserType(0);
    } else {
      setCurrentUserType(1);
    }
  }, [data]);

  const clickHandler = popupPayload => {
    const payloadForPickup = {
      pickup_status: popupPayload.order_status,
      pickup_comment: popupPayload.order_comment,
      is_message: popupPayload?.is_message ? true : false,
    };
    const payloadForReciever = {
      delivery_status: popupPayload.order_status,
      delivery_comment: popupPayload.order_comment,
      is_message: popupPayload?.is_message ? true : false,
    };
    const payload = {
      id: popupPayload.id,
      payload:
        currentUserType === 0
          ? { ...payloadForPickup }
          : { ...payloadForReciever },
    };
    
    const updateStatus = async () => {
      await dispatch(updateOrderStatus(payload))
        .then(res => {
          if (!res?.error) {
            console.log("Status Changed successfully");
          } else {
            toast.error(res?.error?.message || t("something_went_wrong"));
          }
        })
        .catch(err => {
          console.log(err);
        });
      
      onCloseNewCheckIn();
      fetchDashboardData(statusConfig);
      
      if (popupPayload.id === order_id) {
        fetchOrderData(order_id);
      }
    };
    
    updateStatus();
    dispatch(readAllNotification()).then(res => {
      if (!res.error) {
        fetchAllNotification();
      }
    });
  };

  return (
    <>
      <Box sx={{ display: "flex", minHeight: "100vh" }}>
        <AppBar position="fixed" open={true}>
          <Header
            headerTitle={headerTitle}
            showBackBtn={showBackBtn}
            showSearchField={showSearchField}
            handleChange={handleChange}
            AllNotification={AllNotification}
            showNotification={showNotification}
          />
        </AppBar>
        <Drawer
          sx={{
            width: `${drawerWidth}rem`,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: `${drawerWidth}rem`,
              boxSizing: "border-box",
            },
          }}
          variant="permanent"
          anchor="left"
          open={false}>
          <SideMenu />
        </Drawer>
        <Box
          component="main"
          sx={{
            marginTop: "3rem",
            flexGrow: 1,
          }}>
          {children}
        </Box>
      </Box>
      
      {loading ? (
        <CircularProgress sx={{ margin: "20rem 40rem" }} />
      ) : (
        <NewCheckInReqModal
          onClose={onCloseNewCheckIn}
          open={openNewCheckInModal}
          data={data}
          clickHandler={clickHandler}
          currentUserType={currentUserType}
        />
      )}
    </>
  );
};

export default PageLayout;